import { useState, useEffect } from 'react';
import {
  IconShield,
  IconCircleCheck,
  IconCircleX,
  IconPencil,
  IconEye,
  IconLoader2,
} from '@tabler/icons-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import MainCard from '../components/MainCard';
import PageHeader from '../components/PageHeader';

interface User {
  id: number;
  email: string;
  name: string;
  role: string;
  roles?: string[];
  status: string;
  avatarUrl?: string;
  createdAt?: string;
  lastLoginAt?: string;
}

const AVAILABLE_ROLES = ['admin', 'manager', 'support', 'viewer', 'finance', 'account-director', 'campaign-editor'];

export function UserManagementPage() {
  const { user: currentUser, startImpersonation } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actualUser, setActualUser] = useState<any>(null);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [impersonating, setImpersonating] = useState<number | null>(null);

  useEffect(() => {
    checkUserAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only: checkUserAccess is not wrapped in useCallback
  }, []);

  const checkUserAccess = async () => {
    try {
      const res = await api.get('/users/me');
      setActualUser(res.data);
      const isAdmin = res.data.roles?.includes('admin') || res.data.role === 'admin';
      if (isAdmin) {
        loadUsers();
      }
    } catch (err) {
      console.error('Failed to check user access', err);
    } finally {
      setCheckingAccess(false);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/users');
      setUsers(res.data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId: number, newRole: string) => {
    setError('');
    setSuccess('');
    try {
      await api.put(`/users/${userId}/role`, { role: newRole });
      setSuccess('User role updated successfully');
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update role');
    }
  };

  const handleOpenRoleDialog = (user: User) => {
    setSelectedUser(user);
    const currentRoles = user.roles && user.roles.length > 0 ? user.roles : (user.role ? [user.role] : []);
    setSelectedRoles(currentRoles);
    setRoleDialogOpen(true);
  };

  const handleCloseRoleDialog = () => {
    setRoleDialogOpen(false);
    setSelectedUser(null);
    setSelectedRoles([]);
  };

  const handleSaveRoles = async () => {
    if (!selectedUser) return;

    setError('');
    setSuccess('');
    try {
      await api.put(`/users/${selectedUser.id}/roles`, { roles: selectedRoles });
      setSuccess('User roles updated successfully');
      handleCloseRoleDialog();
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update roles');
    }
  };

  const handleStatusChange = async (userId: number, newStatus: string) => {
    setError('');
    setSuccess('');
    try {
      await api.put(`/users/${userId}/status`, { status: newStatus });
      setSuccess('User status updated successfully');
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update status');
    }
  };

  const handleImpersonate = async (userId: number) => {
    setError('');
    setSuccess('');
    setImpersonating(userId);
    try {
      await startImpersonation(userId);
    } catch (err: any) {
      setError(err.message || 'Failed to start impersonation');
      setImpersonating(null);
    }
  };

  const getRoleBadgeVariant = (role: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (role) {
      case 'admin': return 'destructive';
      case 'manager': return 'default';
      case 'support': return 'secondary';
      case 'finance': return 'outline';
      case 'account-director': return 'secondary';
      case 'campaign-editor': return 'outline';
      default: return 'outline';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  if (checkingAccess) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <IconLoader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }

  const isAdmin = actualUser?.roles?.includes('admin') || actualUser?.role === 'admin';
  if (!actualUser || !isAdmin) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertDescription>
            Access denied. Only administrators can access user management.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div>
        <PageHeader
          title="User Management"
          subtitle="Manage user access, roles, and permissions"
        />

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="mb-4">
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        )}

        {/* Role Information */}
        <MainCard className="mb-6">
          <h3 className="text-lg font-semibold mb-3">Role Descriptions</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { role: 'Admin', variant: 'destructive' as const, desc: 'Full access to all features including user management' },
              { role: 'Manager', variant: 'default' as const, desc: 'Access to all features except user management' },
              { role: 'Support', variant: 'secondary' as const, desc: 'Access to customer support features (Customers, Wallet, Transactions, SPAR)' },
              { role: 'Viewer', variant: 'outline' as const, desc: 'Read-only access to Dashboard, Customers, and Email History' },
              { role: 'Finance', variant: 'outline' as const, desc: 'Access to MTN Reconciliation only' },
              { role: 'Account Director', variant: 'secondary' as const, desc: 'Access to customer and account management features (Customers, Products, Distributors, Wallet, Transactions, Reports)' },
            ].map((item) => (
              <div key={item.role} className="flex flex-col min-h-[80px]">
                <Badge variant={item.variant} className="self-start mb-1.5">
                  {item.role}
                </Badge>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </MainCard>

        {/* Users Table */}
        <MainCard>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Roles</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last Login</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <IconLoader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
                  </TableCell>
                </TableRow>
              )}
              {!loading && users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No users found
                  </TableCell>
                </TableRow>
              )}
              {!loading && users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {user.avatarUrl ? (
                        <img
                          src={user.avatarUrl}
                          alt={user.name}
                          className="w-8 h-8 rounded-full object-cover"
                          onError={(e) => {
                            (e.currentTarget as HTMLImageElement).style.display = 'none';
                          }}
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm">
                          {user.name?.charAt(0)}
                        </div>
                      )}
                      {user.name}
                    </div>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 flex-wrap">
                      {(user.roles && user.roles.length > 0 ? user.roles : [user.role]).map((role) => (
                        <Badge
                          key={role}
                          variant={getRoleBadgeVariant(role)}
                        >
                          {role.charAt(0).toUpperCase() + role.slice(1)}
                        </Badge>
                      ))}
                      {user.id !== actualUser?.id && (
                        <Tooltip>
                          <TooltipTrigger
                            render={
                              <Button
                                variant="ghost"
                                size="icon-xs"
                                onClick={() => handleOpenRoleDialog(user)}
                              />
                            }
                          >
                            <IconPencil size={14} />
                          </TooltipTrigger>
                          <TooltipContent>Edit roles</TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Select
                      value={user.status}
                      onValueChange={(val) => { if (val) handleStatusChange(user.id, val); }}
                      disabled={user.id === actualUser?.id}
                    >
                      <SelectTrigger size="sm" className="w-[110px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">
                          <div className="flex items-center gap-1.5">
                            <IconCircleCheck size={14} className="text-green-600" />
                            Active
                          </div>
                        </SelectItem>
                        <SelectItem value="inactive">
                          <div className="flex items-center gap-1.5">
                            <IconCircleX size={14} className="text-red-600" />
                            Inactive
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-sm">{formatDate(user.lastLoginAt)}</TableCell>
                  <TableCell className="text-sm">{formatDate(user.createdAt)}</TableCell>
                  <TableCell>
                    {user.id === actualUser?.id ? (
                      <Badge variant="default">You</Badge>
                    ) : (
                      <Tooltip>
                        <TooltipTrigger
                          render={
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              onClick={() => handleImpersonate(user.id)}
                              disabled={impersonating !== null || user.status !== 'active'}
                            />
                          }
                        >
                          {impersonating === user.id ? (
                            <IconLoader2 size={16} className="animate-spin" />
                          ) : (
                            <IconEye size={16} />
                          )}
                        </TooltipTrigger>
                        <TooltipContent>View as this user</TooltipContent>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </MainCard>

        {/* Instructions */}
        <MainCard className="mt-6">
          <Alert>
            <AlertTitle className="font-semibold">How to add new users:</AlertTitle>
            <AlertDescription>
              1. New users are automatically added to the database when they first log in with Google OAuth<br />
              2. By default, new users are assigned the "Viewer" role (read-only access)<br />
              3. Admins can click the edit icon to assign multiple roles to users<br />
              4. Users get the union of all permissions from all their roles<br />
              5. Only users with @85.group email addresses can log in
            </AlertDescription>
          </Alert>
          <Alert className="mt-3">
            <AlertTitle className="font-semibold">User Impersonation:</AlertTitle>
            <AlertDescription>
              &bull; Click the eye icon next to any user to view the application as that user<br />
              &bull; This allows you to see exactly what permissions and pages they have access to<br />
              &bull; A warning banner will appear at the top showing you're in impersonation mode<br />
              &bull; Click "Stop Impersonating" to return to your admin account<br />
              &bull; Note: You cannot impersonate other admin users for security reasons
            </AlertDescription>
          </Alert>
        </MainCard>

        {/* Role Selection Dialog */}
        <Dialog
          open={roleDialogOpen}
          onOpenChange={(open) => { if (!open) handleCloseRoleDialog(); }}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Roles for {selectedUser?.name}</DialogTitle>
            </DialogHeader>

            <p className="text-sm text-muted-foreground mb-4">
              Select one or more roles. Users will get the union of all permissions from their assigned roles.
            </p>

            <div className="space-y-3">
              {AVAILABLE_ROLES.map((role) => (
                <label key={role} className="flex items-center gap-3 cursor-pointer">
                  <Checkbox
                    checked={selectedRoles.includes(role)}
                    onCheckedChange={(checked) => {
                      setSelectedRoles((prev) =>
                        checked
                          ? [...prev, role]
                          : prev.filter((r) => r !== role)
                      );
                    }}
                  />
                  <span className="text-sm">{role.charAt(0).toUpperCase() + role.slice(1)}</span>
                </label>
              ))}
            </div>

            {selectedRoles.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-3">
                {selectedRoles.map((role) => (
                  <Badge key={role} variant={getRoleBadgeVariant(role)}>
                    {role.charAt(0).toUpperCase() + role.slice(1)}
                  </Badge>
                ))}
              </div>
            )}

            {selectedRoles.length === 0 && (
              <Alert className="mt-3">
                <AlertDescription>
                  At least one role must be selected. Users without roles cannot access the system.
                </AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={handleCloseRoleDialog}>Cancel</Button>
              <Button
                onClick={handleSaveRoles}
                disabled={selectedRoles.length === 0}
              >
                Save Roles
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
